import type { EntityRef } from './history/commands.js';
import type { Building, ProjectDocument, Site } from './types.js';
import { errorText } from './texts.js';

/** What a protected shell keeps: a building's place, size, outline and exterior walls. */
const BUILDING_SHELL = [
  'center',
  'rotation',
  'width',
  'depth',
  'footprint',
  'wallThickness',
  'omittedWalls',
] as const satisfies readonly (keyof Building)[];
const SITE_SHELL = ['boundary'] as const satisfies readonly (keyof Site)[];

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/** The building or parcel whose shell protects `ref` (exterior walls belong to their building). */
export function shellOwner(
  doc: Pick<ProjectDocument, 'buildings' | 'sites' | 'walls'>,
  ref: EntityRef,
): Building | Site | null {
  if (ref.collection === 'sites') return doc.sites[ref.id] ?? null;
  if (ref.collection === 'buildings') return doc.buildings[ref.id] ?? null;
  if (ref.collection === 'walls') {
    const owner = doc.walls[ref.id]?.ownerId;
    return owner ? (doc.buildings[owner] ?? null) : null;
  }
  return null;
}

/** Whether `ref` is part of a protected shell (cannot be moved, turned or reshaped). */
export const isShellLocked = (
  doc: Pick<ProjectDocument, 'buildings' | 'sites' | 'walls'>,
  ref: EntityRef,
): boolean => shellOwner(doc, ref)?.shellLocked === true;

/**
 * Message if a change moved, reshaped or deleted a protected building or parcel (one that is
 * protected before and after the change), otherwise null.
 */
export function shellViolation(before: ProjectDocument, after: ProjectDocument): string | null {
  if (before.buildings === after.buildings && before.sites === after.sites) return null;
  for (const b of Object.values(before.buildings)) {
    if (!b.shellLocked) continue;
    const next = after.buildings[b.id];
    // An office's shell also covers its storey walls removed by hand and its storey outlines.
    const storeysChanged =
      !same(b.office?.openFloors, next?.office?.openFloors) ||
      !same(b.office?.setbacks, next?.office?.setbacks);
    if (
      !next ||
      (next.shellLocked && (BUILDING_SHELL.some((k) => !same(b[k], next[k])) || storeysChanged))
    )
      return errorText('shellLocked', { name: b.name });
  }
  for (const s of Object.values(before.sites)) {
    if (!s.shellLocked) continue;
    const next = after.sites[s.id];
    if (!next || (next.shellLocked && SITE_SHELL.some((k) => !same(s[k], next[k]))))
      return errorText('shellLocked', { name: s.name });
  }
  return null;
}
